import { useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useState, useRef } from 'react'
import { CreditCard, Activity, LogOut, User, Menu, Bell, Sun, Moon } from 'lucide-react'
import { getSettings, listNotifications, getUnreadCount, markAllNotifRead } from '../api'
import { useI18n } from '../i18n'

const ROUTE_TITLES = {
  '/dashboard': 'Tableau de bord',
  '/search': 'Recherche',
  '/crm': 'CRM Pipeline',
  '/contacts-linkedin': 'Contacts LinkedIn (répertoire personnes)',
  '/clients-bydemes': 'Clients Bydemes (intégrateurs/installateurs)',
  '/export': 'Export',
  '/linkedin': 'Posts LinkedIn',
  '/settings': 'Paramètres',
  '/admin-users': 'Utilisateurs & Activité',
}

export default function Header({ onMenuClick }) {
  const location = useLocation()
  const navigate = useNavigate()
  const [credits, setCredits] = useState({ used: 0, limit: 10000 })
  const userEmail = localStorage.getItem('user_email') || ''
  const { t, lang, setLang, theme, setTheme } = useI18n()
  const [unread, setUnread] = useState(0)
  const [notifOpen, setNotifOpen] = useState(false)
  const [notifs, setNotifs] = useState([])
  const notifRef = useRef(null)

  useEffect(() => {
    const poll = () => getUnreadCount().then(r => setUnread(r.data.count)).catch(() => {})
    poll()
    const iv = setInterval(poll, 60000)
    return () => clearInterval(iv)
  }, [])

  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const openNotifs = async () => {
    if (!notifOpen) {
      try {
        const r = await listNotifications()
        setNotifs(r.data)
      } catch {}
    }
    setNotifOpen(o => !o)
  }

  const handleMarkAll = async () => {
    await markAllNotifRead()
    setUnread(0)
    setNotifs(prev => prev.map(n => ({ ...n, is_read: true })))
  }

  useEffect(() => {
    getSettings()
      .then((res) => {
        setCredits({
          used: res.data.pappers_credits_used,
          limit: res.data.pappers_credits_limit,
        })
      })
      .catch(() => {})
  }, [location.pathname])

  const handleLogout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('user_email')
    navigate('/login', { replace: true })
  }

  const title = ROUTE_TITLES[location.pathname] || 'ProspectFlow'
  const pct = Math.min(100, (credits.used / credits.limit) * 100)
  const creditColor = pct > 80 ? 'text-red-400' : pct > 60 ? 'text-yellow-400' : 'text-green-400'

  return (
    <header className="h-14 flex-shrink-0 bg-dark-900 border-b border-dark-700 flex items-center justify-between px-3 sm:px-6 gap-2">
      <div className="flex items-center gap-2 min-w-0">
        {/* Mobile hamburger */}
        <button
          onClick={onMenuClick}
          className="md:hidden p-1.5 text-dark-300 hover:text-dark-50"
          aria-label="Menu"
        >
          <Menu size={22} />
        </button>
        <h2 className="text-dark-50 font-semibold text-sm sm:text-base truncate">{title}</h2>
      </div>

      <div className="flex items-center gap-2 sm:gap-4">
        {/* Credits — hide on small screens */}
        <div className="hidden lg:flex items-center gap-2 text-sm">
          <CreditCard className="w-4 h-4 text-dark-400" />
          <span className="text-dark-400">Pappers :</span>
          <span className={creditColor}>
            {credits.used.toLocaleString('fr-FR')} / {credits.limit.toLocaleString('fr-FR')}
          </span>
          <div className="w-20 h-1.5 bg-dark-700 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${pct > 80 ? 'bg-red-500' : pct > 60 ? 'bg-yellow-500' : 'bg-green-500'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        <div className="hidden sm:flex items-center gap-1.5 text-xs text-dark-500">
          <Activity className="w-3.5 h-3.5 text-green-500" />
          <span>{t('header.online')}</span>
        </div>

        {/* Toggle langue */}
        <button
          onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
          className="px-2 py-1 rounded text-xs font-bold text-dark-300 hover:text-dark-50 border border-dark-700"
          title={t('common.lang')}
        >
          {lang === 'fr' ? 'FR' : 'EN'}
        </button>

        {/* Toggle thème */}
        <button
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          className="p-1.5 text-dark-300 hover:text-dark-50"
          title={t('common.theme')}
        >
          {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
        </button>

        {/* Cloche notifications */}
        <div className="relative" ref={notifRef}>
          <button onClick={openNotifs} className="relative p-1.5 text-dark-300 hover:text-dark-50">
            <Bell size={18} />
            {unread > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-dark-50 text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </button>
          {notifOpen && (
            <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-dark-800 border border-dark-700 rounded-xl shadow-2xl z-50">
              <div className="flex items-center justify-between p-3 border-b border-dark-700">
                <span className="text-dark-50 text-sm font-semibold">Notifications</span>
                {notifs.some(n => !n.is_read) && (
                  <button onClick={handleMarkAll} className="text-xs text-primary-400 hover:text-primary-300">
                    Tout marquer lu
                  </button>
                )}
              </div>
              {notifs.length === 0 ? (
                <p className="p-4 text-dark-500 text-sm text-center">Aucune notification</p>
              ) : (
                notifs.map(n => (
                  <div key={n.id} className={`p-3 border-b border-dark-700/50 ${n.is_read ? 'opacity-60' : ''}`}>
                    <div className="flex items-center gap-2">
                      <span>{n.type === 'signal' ? '📡' : n.type === 'new_prospects' ? '🆕' : 'ℹ️'}</span>
                      <p className="text-dark-50 text-xs font-medium flex-1">{n.title}</p>
                    </div>
                    <p className="text-dark-400 text-xs mt-1 line-clamp-3">{n.body}</p>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 sm:border-l sm:border-dark-700 sm:pl-4">
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-dark-400">
            <User size={13} />
            <span className="max-w-[120px] truncate">{userEmail}</span>
          </div>
          <button
            onClick={handleLogout}
            className="p-1.5 text-dark-500 hover:text-red-400 transition-colors rounded"
            title="Se déconnecter"
          >
            <LogOut size={15} />
          </button>
        </div>
      </div>
    </header>
  )
}
